import { useMemo, useState } from 'react';
import { MenuItem } from '@carbon/react';
import {
  ExportMenuButton,
  SingleLayerSpatialExportDialog,
  type SpatialLayerOption,
} from 'components/export-spatial-data';
import { useTypeCurveSpatialExport } from './hooks';
import { useTypeCurveListingExcelItem } from './type-curves-export-hooks';
import type { TypeCurveHeadersQueryParams } from './atoms';

// The single exportable layer id the backend keys the spatial export on
// (GisExport::Sources::TypeCurveHeader). The Type Curves page exports one spatial dataset — each
// type curve's Type Curve Area polygon.
const TYPE_CURVE_AREAS_LAYER_ID = 'type_curve_areas';

// The type curve attributes offered in the spatial picker. Ids mirror the backend attribute catalog
// (GisExport::Sources::TypeCurveHeader::TC_COLUMNS); only columns the listing is built from —
// type_curve_header_details plus the joined Type Curve Area name — are offered. (Well Count and Section
// EUR are deliberately absent: they are not stored on the detail the listing reads, so the export has no
// authoritative source for them.) Keep in sync with TC_COLUMNS when the catalog changes.
const TYPE_CURVE_EXPORT_ATTRIBUTES = [
  { id: 'unique_tc_id', label: 'Type Curve ID' },
  { id: 'type_curve_area_name', label: 'Type Curve Area' },
  { id: 'basin', label: 'Basin' },
  { id: 'formation', label: 'Formation' },
  { id: 'status', label: 'Status' },
  { id: 'oil_eur_mbbl_30yr', label: 'Oil EUR (MBbl, 30yr)' },
  { id: 'gas_eur_mmcf_30yr', label: 'Gas EUR (MMcf, 30yr)' },
  { id: 'water_eur_mbbl_30yr', label: 'Water EUR (MBbl, 30yr)' },
  { id: 'created_by', label: 'Created By' },
  { id: 'created_at', label: 'Created At' },
  { id: 'updated_at', label: 'Updated At' },
] as const;

interface TypeCurvesExportMenuProps {
  /**
   * The current Type Curves catalog filter params; both exports cover exactly this filtered set.
   * Undefined until the listing's filters load — the items disable themselves ("Loading data…") until then.
   */
  params?: TypeCurveHeadersQueryParams;
  /**
   * The filtered record count. Undefined until the listing's first response lands (treated as loading);
   * zero disables both exports with a reason.
   */
  totalCount?: number;
  /**
   * True while the listing is fetching. The table keeps showing the previous rows meanwhile, so
   * `params` and `totalCount` can describe different sets; the Excel item holds, with a loading
   * label, until they agree again.
   */
  fetching?: boolean;
}

/**
 * The Type Curves page "Export" dropdown (the page-header action on the Type Curves tab). A single
 * {@link ExportMenuButton} offering two exports of the current filtered set:
 *
 *  - **Spatial Data** — the GIS export of each type curve's Type Curve Area polygon, via the shared
 *    {@link SingleLayerSpatialExportDialog} (format + attribute picker, async status banner).
 *  - **Excel** — the tabular export of the filtered Type Curves listing, owned by
 *    {@link useTypeCurveListingExcelItem} (its request, in-progress label, and lifecycle toast).
 *
 * Replaces the two standalone header buttons ("Export Spatial Data" + "Export Filtered Type Curves").
 * Scoped to the current catalog filters; each item disables itself when its export is unavailable, and
 * the whole trigger disables (with a reason) when neither can run.
 *
 * @param {TypeCurvesExportMenuProps} props - The current filter params + filtered record count.
 * @return {JSX.Element} The "Export" menu button, its items, and the spatial-export dialog.
 */
export function TypeCurvesExportMenu({
  params,
  totalCount,
  fetching = false,
}: TypeCurvesExportMenuProps) {
  const spatial = useTypeCurveSpatialExport();
  const [spatialOpen, setSpatialOpen] = useState(false);

  // Undefined params or count means the listing hasn't loaded yet — show a loading state rather than
  // "no type curves match" (which a premature count of 0 would otherwise read as).
  const loading = !params || totalCount === undefined;
  const count = totalCount ?? 0;

  const excel = useTypeCurveListingExcelItem(params, count, loading, fetching);

  const spatialDisabled = loading || !spatial.userId || count === 0;
  // The whole trigger is only disabled when neither export can run; otherwise it opens and the
  // unavailable item shows disabled.
  const allDisabled = spatialDisabled && excel.disabled;
  const disabledReason = loading
    ? 'Loading data...'
    : count === 0
      ? 'No type curves match the current filters'
      : undefined;

  const layer = useMemo<SpatialLayerOption>(
    () => ({
      id: TYPE_CURVE_AREAS_LAYER_ID,
      label: 'Type Curve Areas',
      count: totalCount,
      attributes: TYPE_CURVE_EXPORT_ATTRIBUTES.map((attribute) => ({
        ...attribute,
      })),
    }),
    [totalCount]
  );

  return (
    <>
      <ExportMenuButton disabled={allDisabled} disabledReason={disabledReason}>
        <MenuItem
          label="Spatial Data"
          disabled={spatialDisabled}
          onClick={() => setSpatialOpen(true)}
        />
        <MenuItem
          label={excel.label}
          disabled={excel.disabled}
          onClick={excel.onClick}
        />
      </ExportMenuButton>
      {/* Only mounted once the filters have loaded — the Spatial Data item is disabled until then, so the
          dialog can never open with a `{}` filter that would export every type curve (INS-13632). */}
      {params && (
        <SingleLayerSpatialExportDialog
          open={spatialOpen}
          onClose={() => setSpatialOpen(false)}
          layer={layer}
          filters={params as Record<string, unknown>}
          controller={spatial}
        />
      )}
      {excel.toast}
    </>
  );
}
